// ============= Imports =============
import { AuthContextProps, tokenManager } from '@tw/tw-component-library';
import { ApiResponse } from '..//types';

// ============= Types =============
interface GetBillParams {
  billNumber: string;
  getAccessToken: AuthContextProps['getAccessToken'];
}

// ============= Constants =============
const IS_DEVELOPMENT = import.meta.env.MODE === 'development';
const PROD_BASE_URL = import.meta.env.VITE_REQUEST_URL;
const API_ENDPOINT = '/api/bill';
const SIDETRADE_SCOPE =
  'api://b922470e-04e2-4c04-84fc-e5ec591459ad/SidetradeFrontEndProd';
const HEADERS = {
  'x-functions-key': 'Test',
  'Content-Type': 'application/json',
};

// ============= Utility Functions =============
/**
 * Creates headers for API bills including authentication
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
});

const constructApiUrl = (billNumber: string): string => {
  if (IS_DEVELOPMENT) {
    // In development, use relative URL for proxy
    return `${API_ENDPOINT}?billNumber=${encodeURIComponent(billNumber)}`;
  } else {
    // In production, use full URL
    return `${PROD_BASE_URL}${API_ENDPOINT}?billNumber=${encodeURIComponent(
      billNumber
    )}`;
  }
};

// ============= Main Function =============
export const getBillsByNumber = async ({
  billNumber,
  getAccessToken,
}: GetBillParams): Promise<ApiResponse<any>> => {
  try {
    console.log(
      `🔄 Fetching API data in ${
        IS_DEVELOPMENT ? 'development' : 'production'
      } mode...`
    );

    // Token acquisition with required scopes
    const token = await getAccessToken([SIDETRADE_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire Graph API token');
    }
    tokenManager.logTokenInfo(token, 'Graph API');

    // Get the appropriate URL based on environment
    const url = constructApiUrl(billNumber);

    console.log('🌐 Making request to:', url);
    console.log('Using billnumber:', billNumber);

    // Configure fetch options based on environment
    const fetchOptions: RequestInit = {
      method: 'GET',
      headers: createHeaders(token),
      // Include credentials only in development for proxy
      ...(IS_DEVELOPMENT && { credentials: 'include' }),
    };

    const response = await fetch(url, fetchOptions);

    // Response handling
    if (!response.ok) {
      // const errorText = await response.text();
      if (response.status === 400) {
        console.error('Bad request - check your input parameters');
        throw new Error('Bad request - check your input parameters');
      }
      if (response.status === 401) {
        console.error('Unauthorized access - check your credentials');
        throw new Error('Unauthorized access - check your credentials');
      }
      if (response.status === 404) {
        console.error(response.status);
        throw new Error('Bill not found - check the bill number');
      }
      if (response.status === 500) {
        console.error('Server error - please try again later');
        throw new Error('Server error - please try again later');
      }
      if (response.status === 503) {
        console.error('Service unavailable - please try again later');
        throw new Error('Service unavailable - please try again later');
      }
    }

    const data = await response.json();
    console.log('✅ API data fetched successfully');

    return {
      success: true,
      data,
    };
  } catch (error) {
    console.error('🔴 Error fetching API data:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};
